/**
 * ════════════════════════════════════════════════════════
 * MTM CONFIGURATOR v4 — ATICA MAN
 * ════════════════════════════════════════════════════════
 * 4-step: Fabric → Garment → Customize → Review
 * + Stock Location toggle (Regional / Mill Direct)
 * + Variable-price fabrics
 */

(function () {
  'use strict';

  const DATA = window.__MTM_DATA__;
  if (!DATA) { console.error('MTM: No __MTM_DATA__ found.'); return; }

  const TIER = DATA.tier;
  const VARIANTS = DATA.variants;
  const CUSTOMS = Object.keys(DATA.customizations).length
    ? DATA.customizations
    : DATA.defaultCustomizations;

  const TOTAL_STEPS = 4;
  let currentStep = 1;
  let currentStockLocation = 'regional';
  let currentMillFilter = 'all';

  // ── Order State ──
  const order = {
    variantId: null,
    fabricTitle: '',
    fabricMill: '',
    fabricWeight: '',
    fabricPrice: 0,
    fabricPriceIsVariable: false,
    fabricPriceMin: 0,
    fabricPriceMax: 0,
    stockLocation: 'regional',

    garments: {
      blazer: false,
      extra_pants: false,
      vest: false,
    },
    garmentPrices: {
      extraPantsPrice: 0,
      vestPrice: 0,
      blazerAdjustment: 0,
    },

    customizations: {},
    monogramText: '',
    monogramColor: 'Gold',
  };

  // Customizations all start unselected — user chooses which to enable
  // Items with $0 price at this tier will show as "Included" when toggled on

  // ════════════════════════════════════════════════════════
  // NAVIGATION
  // ════════════════════════════════════════════════════════

  function goTo(n) {
    if (n < 1 || n > TOTAL_STEPS || n === currentStep) return;

    if (currentStep === 1 && n > 1) {
      if (!order.variantId) {
        alert('Please select a fabric before continuing.');
        return;
      }
      if (order.fabricPriceIsVariable) {
        if (!order.fabricPrice || order.fabricPrice < order.fabricPriceMin) {
          alert('Please enter a valid suit price (minimum ' + formatMoney(order.fabricPriceMin) + ').');
          return;
        }
        if (order.fabricPriceMax > 0 && order.fabricPrice > order.fabricPriceMax) {
          alert('Price cannot exceed ' + formatMoney(order.fabricPriceMax) + '.');
          return;
        }
      }
    }

    currentStep = n;
    document.querySelectorAll('.mtm-panel').forEach(p => p.classList.remove('active'));
    const panel = document.querySelector(`[data-panel="${n}"]`);
    if (panel) panel.classList.add('active');

    renderStepNav();
    if (n === 2) updateGarmentPrices();
    if (n === 3) renderCustomizations();
    if (n === 4) renderSummary();
    updateFloatingPrice();

    const cfg = document.getElementById('configure');
    if (cfg) cfg.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function next() { goTo(currentStep + 1); }
  function prev() { goTo(currentStep - 1); }

  function renderStepNav() {
    document.querySelectorAll('.mtm-step-ind').forEach(el => {
      const s = parseInt(el.dataset.step);
      el.classList.remove('active', 'done');
      if (s === currentStep) el.classList.add('active');
      else if (s < currentStep) el.classList.add('done');
      const dot = el.querySelector('.mtm-step-dot');
      if (dot) dot.textContent = s < currentStep ? '✓' : s;
    });
  }

  // ════════════════════════════════════════════════════════
  // STOCK LOCATION
  // ════════════════════════════════════════════════════════

  function setStockLocation(loc, btn) {
    currentStockLocation = loc;
    order.stockLocation = loc;

    // Update toggle buttons
    document.querySelectorAll('.mtm-stock-btn').forEach(b => b.classList.remove('active'));
    if (btn) btn.classList.add('active');

    // Update note
    const note = document.getElementById('mtmStockNote');
    if (note) {
      note.textContent = loc === 'regional'
        ? 'Fabrics available from regional warehouse. Fastest delivery.'
        : 'Sourced directly from the mill. Extended lead time may apply.';
    }

    // Clear current fabric selection
    order.variantId = null;
    order.fabricTitle = '';
    order.fabricMill = '';
    order.fabricWeight = '';
    order.fabricPrice = 0;
    order.fabricPriceIsVariable = false;
    document.querySelectorAll('.mtm-fabric-card').forEach(c => c.classList.remove('selected'));

    // Hide variable price input
    const vpContainer = document.getElementById('mtmVariablePriceInput');
    if (vpContainer) vpContainer.style.display = 'none';

    // Reset mill filter
    currentMillFilter = 'all';
    document.querySelectorAll('.mtm-fab-btn').forEach(b => b.classList.remove('active'));
    const allBtn = document.querySelector('.mtm-fab-btn');
    if (allBtn) allBtn.classList.add('active');

    applyFabricFilters();
    updateFloatingPrice();
  }

  // ════════════════════════════════════════════════════════
  // STEP 1: FABRIC
  // ════════════════════════════════════════════════════════

  function applyFabricFilters() {
    document.querySelectorAll('.mtm-fabric-card').forEach(card => {
      const stockMatch = card.dataset.stock === currentStockLocation;
      const millMatch = currentMillFilter === 'all' || card.dataset.mill === currentMillFilter;
      card.style.display = (stockMatch && millMatch) ? '' : 'none';
    });
  }

  function selectFabric(variantId) {
    const v = VARIANTS.find(v => v.id === variantId);
    if (!v) return;

    // Ensure this variant matches current stock filter
    if (v.stockLocation !== currentStockLocation) return;

    order.variantId = v.id;
    order.fabricTitle = v.title;
    order.fabricMill = v.mill;
    order.fabricWeight = v.weight;
    order.stockLocation = v.stockLocation;
    order.garmentPrices.extraPantsPrice = v.extraPantsPrice;
    order.garmentPrices.vestPrice = v.vestPrice;
    order.garmentPrices.blazerAdjustment = v.blazerAdjustment;

    // Variable vs fixed
    order.fabricPriceIsVariable = !!v.variablePrice;
    order.fabricPriceMin = v.price;
    order.fabricPriceMax = v.priceMax || 0;

    const vpContainer = document.getElementById('mtmVariablePriceInput');
    const vpInput = document.getElementById('mtmVpInput');
    const vpDesc = document.getElementById('mtmVpDesc');
    const vpRange = document.getElementById('mtmVpRange');

    if (v.variablePrice) {
      order.fabricPrice = 0;
      if (vpContainer) vpContainer.style.display = 'block';
      if (vpDesc) vpDesc.textContent = v.title + ' — ' + v.mill;
      if (vpRange) {
        let rangeText = 'Range: ' + formatMoney(v.price) + ' minimum';
        if (v.priceMax > 0) rangeText += ' — ' + formatMoney(v.priceMax) + ' maximum';
        vpRange.textContent = rangeText;
      }
      if (vpInput) {
        vpInput.value = '';
        vpInput.min = (v.price / 100).toFixed(0);
        if (v.priceMax > 0) vpInput.max = (v.priceMax / 100).toFixed(0);
        else vpInput.removeAttribute('max');
        vpInput.focus();
      }
    } else {
      order.fabricPrice = v.price;
      if (vpContainer) vpContainer.style.display = 'none';
    }

    document.querySelectorAll('.mtm-fabric-card').forEach(card => {
      card.classList.toggle('selected', parseInt(card.dataset.variantId) === variantId);
    });

    updateFloatingPrice();
  }

  function setVariablePrice(val) {
    const dollars = parseFloat(val);
    order.fabricPrice = (isNaN(dollars) || dollars <= 0) ? 0 : Math.round(dollars * 100);
    updateFloatingPrice();

    const input = document.getElementById('mtmVpInput');
    if (!input) return;
    const cents = order.fabricPrice;
    const valid = cents >= order.fabricPriceMin && (order.fabricPriceMax === 0 || cents <= order.fabricPriceMax);
    input.classList.toggle('mtm-vp-invalid', cents > 0 && !valid);
    input.classList.toggle('mtm-vp-valid', cents > 0 && valid);
  }

  function filterFabrics(mill, btn) {
    currentMillFilter = mill;
    document.querySelectorAll('.mtm-fab-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    applyFabricFilters();
  }

  // ════════════════════════════════════════════════════════
  // STEP 2: GARMENT
  // ════════════════════════════════════════════════════════

  function toggleGarment(key, el) {
    order.garments[key] = !order.garments[key];
    if (el) el.classList.toggle('selected', order.garments[key]);
    updateFloatingPrice();
  }

  function updateGarmentPrices() {
    const p = order.garmentPrices;
    const fmt = (cents) => cents >= 0 ? '+' + formatMoney(cents) : '−' + formatMoney(Math.abs(cents));
    const el2p = document.getElementById('price2piece');
    const elBlazer = document.getElementById('priceBlazer');
    const elPants = document.getElementById('priceExtraPants');
    const elVest = document.getElementById('priceVest');
    if (el2p) el2p.textContent = order.fabricPrice > 0 ? formatMoney(order.fabricPrice) : '—';
    if (elBlazer) elBlazer.textContent = fmt(p.blazerAdjustment);
    if (elPants) elPants.textContent = fmt(p.extraPantsPrice);
    if (elVest) elVest.textContent = fmt(p.vestPrice);
  }

  // ════════════════════════════════════════════════════════
  // STEP 3: CUSTOMIZATIONS
  // ════════════════════════════════════════════════════════

  function renderCustomizations() {
    const grid = document.getElementById('mtmCustomGrid');
    if (!grid) return;

    let html = '';
    Object.entries(CUSTOMS).forEach(([id, c]) => {
      const priceCents = c.prices[TIER];
      if (priceCents === null || priceCents === undefined) return;
      const includedAtTier = priceCents === 0 && !c.alwaysPaid;
      const isActive = !!order.customizations[id];
      const cls = isActive ? (includedAtTier ? 'active included' : 'active') : '';
      const priceDisplay = includedAtTier ? 'Included' : priceCents === 0 ? 'Free' : '+' + formatMoney(priceCents);

      html += `
        <div class="mtm-custom-card ${cls}"
             data-custom-id="${id}"
             onclick="MTM.toggleCustom('${id}')">
          <div class="mtm-custom-left">
            <div class="mtm-custom-name">${c.name}</div>
            <div class="mtm-custom-desc">${c.desc}</div>
          </div>
          <div class="mtm-custom-right">
            <div class="mtm-custom-price">${priceDisplay}</div>
            <div class="mtm-custom-toggle"></div>
          </div>
        </div>`;
    });
    grid.innerHTML = html;

    const monogramFields = document.getElementById('mtmMonogramFields');
    const monogramC = CUSTOMS.monogram;
    if (monogramFields && monogramC) {
      monogramFields.style.display = order.customizations.monogram ? 'block' : 'none';
    }
  }

  function toggleCustom(id) {
    order.customizations[id] = !order.customizations[id];
    renderCustomizations();
    updateFloatingPrice();
  }

  // ════════════════════════════════════════════════════════
  // PRICING
  // ════════════════════════════════════════════════════════

  function calcTotal() {
    let total = order.fabricPrice;
    if (order.garments.blazer) total += order.garmentPrices.blazerAdjustment;
    if (order.garments.extra_pants) total += order.garmentPrices.extraPantsPrice;
    if (order.garments.vest) total += order.garmentPrices.vestPrice;

    Object.entries(CUSTOMS).forEach(([id, c]) => {
      const priceCents = c.prices[TIER];
      const included = priceCents === 0 && !c.alwaysPaid;
      if (included) return;
      if (order.customizations[id] && priceCents > 0) total += priceCents;
    });
    return total;
  }

  function formatMoney(cents) {
    return '$' + Number((cents / 100).toFixed(0)).toLocaleString();
  }

  function updateFloatingPrice() {
    const fp = document.getElementById('mtmFloatingPrice');
    const amount = document.getElementById('mtmFpAmount');
    if (!fp || !amount) return;
    const total = calcTotal();
    amount.textContent = total > 0 ? formatMoney(total) : '—';
    fp.classList.toggle('visible', total > 0 && currentStep > 0 && currentStep < TOTAL_STEPS);
  }

  // ════════════════════════════════════════════════════════
  // STEP 4: SUMMARY
  // ════════════════════════════════════════════════════════

  function renderSummary() {
    const el = document.getElementById('mtmSummary');
    if (!el) return;

    const total = calcTotal();
    const stockLabel = order.stockLocation === 'mill_direct' ? 'Mill Direct' : 'Regional Stock';
    let html = '';

    html += `<div class="mtm-summary__head"><h3>${DATA.productTitle}</h3></div>`;
    html += `<div class="mtm-summary__body">`;

    html += sectionTitle('Fabric');
    html += sumRow('Fabric', order.fabricTitle || 'Not selected');
    html += sumRow('Mill', order.fabricMill || '—');
    html += sumRow('Stock Location', stockLabel);

    html += sectionTitle('Garment');
    if (order.garments.blazer) {
      const adj = order.garmentPrices.blazerAdjustment;
      const blazerPrice = order.fabricPrice + adj;
      html += `<div class="mtm-sum-row">
        <span class="mtm-sum-label">Blazer Only</span>
        <span class="mtm-sum-value">
          <span class="mtm-sum-strikethrough">${formatMoney(order.fabricPrice)}</span>
          ${formatMoney(blazerPrice)}
        </span>
      </div>`;
      html += `<div class="mtm-sum-row">
        <span class="mtm-sum-label mtm-sum-discount-label">Blazer only adjustment</span>
        <span class="mtm-sum-value mtm-sum-discount">${formatMoney(adj)}</span>
      </div>`;
    } else {
      const priceLabel = order.fabricPriceIsVariable ? '2-Piece Suit (custom price)' : '2-Piece Suit';
      html += sumRow(priceLabel, formatMoney(order.fabricPrice));
    }
    if (order.garments.extra_pants) html += sumRow('Extra Trousers', '+' + formatMoney(order.garmentPrices.extraPantsPrice));
    if (order.garments.vest) html += sumRow('Vest', '+' + formatMoney(order.garmentPrices.vestPrice));

    html += sectionTitle('Customizations');
    let hasCustom = false;
    Object.entries(CUSTOMS).forEach(([id, c]) => {
      if (!order.customizations[id]) return;
      hasCustom = true;
      const priceCents = c.prices[TIER];
      const includedAtTier = priceCents === 0 && !c.alwaysPaid;
      html += includedAtTier
        ? sumRow(c.name, '<span class="mtm-sum-included">Included</span>')
        : sumRow(c.name, '+' + formatMoney(priceCents));
    });
    if (order.monogramText) html += sumRow('Monogram Initials', '"' + order.monogramText.toUpperCase() + '" — ' + order.monogramColor);
    if (!hasCustom) html += sumRow('—', 'No add-ons selected');

    html += `</div>`;
    html += `
      <div class="mtm-summary__total">
        <span class="mtm-summary__total-label">Total</span>
        <span class="mtm-summary__total-price">${formatMoney(total)}</span>
      </div>
      <div class="mtm-summary__timeline">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        <span>${order.stockLocation === 'mill_direct'
          ? 'Mill Direct — estimated 6–10 weeks from order confirmation'
          : 'Estimated delivery: 4–6 weeks from order confirmation'}</span>
      </div>`;

    el.innerHTML = html;
  }

  function sectionTitle(t) { return `<div class="mtm-sum-section-title">${t}</div>`; }
  function sumRow(l, v) { return `<div class="mtm-sum-row"><span class="mtm-sum-label">${l}</span><span class="mtm-sum-value">${v}</span></div>`; }

  // ════════════════════════════════════════════════════════
  // CREATE DRAFT ORDER
  // ════════════════════════════════════════════════════════

  // ── Configuration: set these after deploying the Cloudflare Worker ──
  const WORKER_URL = DATA.workerUrl || '';
  const WORKER_SECRET = DATA.workerSecret || '';

  async function addToCart() {
    if (!order.variantId) { alert('Please select a fabric.'); return; }
    if (order.fabricPriceIsVariable && (!order.fabricPrice || order.fabricPrice < order.fabricPriceMin)) {
      alert('Please enter a valid suit price.'); return;
    }
    if (!WORKER_URL) {
      alert('Configuration error: Worker URL not set. Contact admin.');
      return;
    }

    const btnText = document.getElementById('mtmCartBtnText');
    const btnLoading = document.getElementById('mtmCartBtnLoading');
    const btn = document.getElementById('mtmAddToCart');
    if (btnText) btnText.style.display = 'none';
    if (btnLoading) btnLoading.style.display = 'inline';
    if (btn) btn.disabled = true;

    // ── Build line items for itemized draft order ──
    const lineItems = [];

    // Main suit line item (the fabric variant)
    let suitTitle;
    if (order.garments.blazer) {
      const adj = order.garmentPrices.blazerAdjustment;
      const blazerPrice = order.fabricPrice + adj;
      suitTitle = 'MTM Blazer — ' + order.fabricTitle
        + ' | Blazer Only Adjustment: ' + formatMoney(order.fabricPrice) + ' ' + formatMoney(adj) + ' = ' + formatMoney(blazerPrice);
    } else {
      suitTitle = 'MTM 2-Piece Suit — ' + order.fabricTitle;
    }

    const suitProperties = [
      { name: 'Tier', value: TIER.charAt(0).toUpperCase() + TIER.slice(1) },
      { name: 'Fabric', value: order.fabricTitle },
      { name: 'Mill', value: order.fabricMill },
      { name: 'Stock', value: order.stockLocation === 'mill_direct' ? 'Mill Direct' : 'Regional Stock' },
    ];

    if (order.monogramText) {
      suitProperties.push({ name: 'Monogram', value: order.monogramText.toUpperCase() + ' — ' + order.monogramColor });
    }

    // Base suit price (fabric price + blazer adjustment if blazer only)
    let suitPriceCents = order.fabricPrice;
    if (order.garments.blazer) {
      const adj = order.garmentPrices.blazerAdjustment;
      suitProperties.push({ name: 'Blazer Only Adjustment', value: formatMoney(order.fabricPrice) + ' ' + formatMoney(adj) + ' = ' + formatMoney(order.fabricPrice + adj) });
      suitPriceCents += adj;
    }

    lineItems.push({
      title: suitTitle,
      priceCents: suitPriceCents,
      properties: suitProperties,
    });

    // Extra Trousers as separate line item
    if (order.garments.extra_pants) {
      lineItems.push({
        title: 'Extra Trousers — ' + order.fabricTitle,
        priceCents: order.garmentPrices.extraPantsPrice,
        properties: [],
      });
    }

    // Vest as separate line item
    if (order.garments.vest) {
      lineItems.push({
        title: 'Vest — ' + order.fabricTitle,
        priceCents: order.garmentPrices.vestPrice,
        properties: [],
      });
    }

    // Customizations as separate line items (only user-selected ones)
    Object.entries(CUSTOMS).forEach(([id, c]) => {
      if (!order.customizations[id]) return;
      const priceCents = c.prices[TIER];
      const includedAtTier = priceCents === 0 && !c.alwaysPaid;
      const isShipping = id === 'shipping' || c.name.toLowerCase().includes('ship to');

      lineItems.push({
        title: c.name + (includedAtTier ? ' (Included)' : ''),
        priceCents: priceCents,
        properties: [],
        isShipping: isShipping,
      });
    });

    // Build tags
    const tags = ['mtm', 'mtm-' + TIER];
    if (order.stockLocation === 'mill_direct') tags.push('mill-direct');
    else tags.push('regional-stock');

    // Build note with selected features
    const noteLines = [];
    noteLines.push('Tier: ' + TIER.charAt(0).toUpperCase() + TIER.slice(1));
    noteLines.push('Fabric: ' + order.fabricTitle + ' (' + order.fabricMill + ')');
    noteLines.push('Stock: ' + (order.stockLocation === 'mill_direct' ? 'Mill Direct' : 'Regional Stock'));

    const selectedIncluded = [];
    Object.entries(CUSTOMS).forEach(([id, c]) => {
      if (!order.customizations[id]) return;
      const priceCents = c.prices[TIER];
      const includedAtTier = priceCents === 0 && !c.alwaysPaid;
      if (includedAtTier) selectedIncluded.push(c.name);
    });
    if (selectedIncluded.length > 0) {
      noteLines.push('');
      noteLines.push('— Included at tier (no charge) —');
      selectedIncluded.forEach(name => noteLines.push('✓ ' + name));
    }

    // Build structured spec data for Google Sheet
    const specCustomizations = [];
    let specShipping = 'No';
    Object.entries(CUSTOMS).forEach(([id, c]) => {
      if (!order.customizations[id]) return;
      const priceCents = c.prices[TIER];
      const isShipping = id === 'shipping' || c.name.toLowerCase().includes('ship to');
      if (isShipping) {
        specShipping = formatMoney(priceCents);
        return;
      }
      const includedAtTier = priceCents === 0 && !c.alwaysPaid;
      specCustomizations.push({
        title: c.name,
        price: includedAtTier ? '$0' : formatMoney(priceCents),
      });
    });

    const specData = {
      tier: TIER.charAt(0).toUpperCase() + TIER.slice(1),
      fabric: order.fabricTitle,
      mill: order.fabricMill,
      stockLocation: order.stockLocation === 'mill_direct' ? 'Mill Direct' : 'Regional Stock',
      garmentType: order.garments.blazer ? 'Blazer Only' : '2-Piece Suit',
      basePrice: formatMoney(order.fabricPrice),
      blazerAdjustment: order.garments.blazer ? formatMoney(order.garmentPrices.blazerAdjustment) : '',
      extraTrousers: order.garments.extra_pants,
      extraTrousersPrice: order.garments.extra_pants ? formatMoney(order.garmentPrices.extraPantsPrice) : '',
      vest: order.garments.vest,
      vestPrice: order.garments.vest ? formatMoney(order.garmentPrices.vestPrice) : '',
      customizations: specCustomizations,
      monogram: order.monogramText ? order.monogramText.toUpperCase() + ' — ' + order.monogramColor : '',
      shipping: specShipping,
    };

    try {
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Worker-Secret': WORKER_SECRET,
        },
        body: JSON.stringify({
          lineItems,
          tags: tags.join(', '),
          note: noteLines.join('\n'),
          specData,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create order');
      }

      // Show success with draft order details
      showOrderConfirmation(result);

    } catch (error) {
      console.error('MTM Order Error:', error);
      alert('Error creating order. Please try again.\n\n' + error.message);
      if (btnText) btnText.style.display = 'inline';
      if (btnLoading) btnLoading.style.display = 'none';
      if (btn) btn.disabled = false;
    }
  }

  function showOrderConfirmation(result) {
    const panel = document.querySelector(`[data-panel="4"]`);
    if (!panel) return;

    const total = calcTotal();
    const stockLabel = order.stockLocation === 'mill_direct' ? 'Mill Direct' : 'Regional Stock';

    panel.innerHTML = `
      <div class="mtm-confirmation">
        <div class="mtm-confirmation__icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="8 12 11 15 16 9"/>
          </svg>
        </div>
        <h2 class="mtm-confirmation__title">Order Created Successfully</h2>
        <p class="mtm-confirmation__order-name">${result.draftOrderName}</p>

        <div class="mtm-confirmation__details">
          <div class="mtm-sum-row"><span class="mtm-sum-label">Fabric</span><span class="mtm-sum-value">${order.fabricTitle}</span></div>
          <div class="mtm-sum-row"><span class="mtm-sum-label">Mill</span><span class="mtm-sum-value">${order.fabricMill}</span></div>
          <div class="mtm-sum-row"><span class="mtm-sum-label">Stock</span><span class="mtm-sum-value">${stockLabel}</span></div>
          <div class="mtm-sum-row"><span class="mtm-sum-label">Total</span><span class="mtm-sum-value"><strong>${formatMoney(total)}</strong></span></div>
        </div>

        <div class="mtm-confirmation__actions">
          <a href="${result.adminUrl}" target="_blank" class="mtm-btn mtm-btn-primary">
            View Draft Order in Admin
          </a>
          <button class="mtm-btn mtm-btn-secondary" onclick="location.reload()">
            Create Another Order
          </button>
        </div>

        <p class="mtm-confirmation__note">
          Complete the sale from the Draft Order in Shopify Admin or POS.
        </p>
      </div>
    `;
  }

  // ── Init: apply stock filter on load ──
  document.addEventListener('DOMContentLoaded', () => applyFabricFilters());

  // ════════════════════════════════════════════════════════
  // PUBLIC API
  // ════════════════════════════════════════════════════════

  window.MTM = {
    order, goTo, next, prev,
    selectFabric, setVariablePrice, filterFabrics,
    setStockLocation, toggleGarment, toggleCustom, addToCart,
  };

})();
